import { Link } from 'react-router-dom';
import { Award, MapPin, ShieldCheck, Target, Users } from 'lucide-react';

const values = [
  {
    icon: Target,
    title: 'Purposeful service',
    description:
      'The goal is to make technology easier to rely on, easier to understand, and easier to move forward with.',
  },
  {
    icon: Users,
    title: 'Experienced support',
    description:
      'Connectify brings practical experience across support, networking, infrastructure, websites, and technical installs.',
  },
  {
    icon: Award,
    title: 'Quality delivery',
    description:
      'Each project is handled with a focus on reliability, clear workmanship, and outcomes that last.',
  },
  {
    icon: MapPin,
    title: 'Regional flexibility',
    description:
      'Services are delivered across multiple cities and broader Australian service regions with a tailored approach.',
  },
];

const expertiseItems = [
  'Network design and implementation',
  'IT support and troubleshooting',
  'Custom website development',
  'Security systems and surveillance',
  'System administration',
  'Cloud services',
  'Backup and recovery planning',
  'Hardware and installation work',
  'Technical consultation and planning',
];

const storyPoints = [
  'Support that feels clear instead of overwhelming',
  'Practical recommendations shaped around the real job',
  'Technology services for homes, businesses, and growing operations',
];

const About = () => {
  return (
    <div className="overflow-hidden bg-[linear-gradient(180deg,#f7fbff_0%,#eef6ff_22%,#ffffff_50%,#f6fbff_100%)] pt-16 text-slate-900 dark:bg-[linear-gradient(180deg,#08111f_0%,#0b1729_35%,#0a1220_100%)] dark:text-white">
      <section
        className="relative isolate overflow-hidden border-b border-white/50 dark:border-white/10"
        data-testid="about-hero"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-[-8rem] top-24 h-72 w-72 rounded-full bg-cyan-300/30 blur-3xl dark:bg-cyan-400/10" />
          <div className="absolute right-[-6rem] top-12 h-80 w-80 rounded-full bg-blue-500/20 blur-3xl dark:bg-blue-500/20" />
          <div className="absolute bottom-[-5rem] left-1/3 h-64 w-64 rounded-full bg-emerald-300/20 blur-3xl dark:bg-emerald-400/10" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.8),transparent_45%)] dark:bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.08),transparent_35%)]" />
        </div>

        <div className="relative mx-auto grid min-h-[calc(100vh-4rem)] max-w-7xl items-center gap-14 px-4 py-20 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:px-8">
          <div className="max-w-3xl">
            <div className="mb-6 inline-flex items-center rounded-full border border-sky-200 bg-white/80 px-4 py-2 text-sm font-medium text-sky-700 shadow-sm backdrop-blur dark:border-sky-400/20 dark:bg-white/5 dark:text-sky-200">
              About Connectify across multiple cities and Australian service regions
            </div>
            <h1 className="max-w-3xl text-4xl font-black tracking-tight text-slate-950 sm:text-5xl lg:text-7xl dark:text-white">
              Technology help shaped by
              <span className="block bg-gradient-to-r from-sky-500 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                practical thinking, clear support, and steady delivery.
              </span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-600 dark:text-slate-300">
              Connectify IT Services exists to make technical work feel more manageable, whether
              that means solving support issues, improving infrastructure, delivering installations,
              or helping clients move forward with the right setup.
            </p>

            <div className="mt-10 grid gap-4 sm:grid-cols-3">
              {storyPoints.map((point) => (
                <div
                  key={point}
                  className="rounded-2xl border border-white/70 bg-white/85 p-5 shadow-[0_20px_50px_-30px_rgba(15,23,42,0.4)] backdrop-blur dark:border-white/10 dark:bg-white/5"
                >
                  <p className="text-sm leading-6 text-slate-700 dark:text-slate-200">{point}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-sky-400/20 via-cyan-300/10 to-emerald-300/20 blur-2xl dark:from-sky-400/15 dark:to-emerald-400/10" />
            <div className="relative overflow-hidden rounded-[2rem] border border-white/70 bg-slate-950 p-8 text-white shadow-[0_30px_80px_-24px_rgba(2,12,27,0.7)] dark:border-white/10">
              <div className="flex items-center justify-between border-b border-white/10 pb-5">
                <div>
                  <p className="text-sm uppercase tracking-[0.25em] text-sky-200/80">Connectify</p>
                  <h2 className="mt-2 text-2xl font-bold">How we work</h2>
                </div>
                <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-sm text-sky-100">
                  Built on trust
                </div>
              </div>

              <div className="mt-6 space-y-4">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                  <p className="text-sm uppercase tracking-[0.22em] text-cyan-200">Approach</p>
                  <p className="mt-2 text-lg font-semibold">
                    Clear communication, thoughtful planning, and practical execution.
                  </p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                  <p className="text-sm uppercase tracking-[0.22em] text-cyan-200">Focus</p>
                  <p className="mt-2 text-lg font-semibold">
                    Reliable outcomes for support, installations, infrastructure, and business tech.
                  </p>
                </div>
                <div className="rounded-2xl border border-emerald-400/20 bg-emerald-400/10 p-5 text-sm text-emerald-100">
                  Good service is not just about technical knowledge. It is also about making the
                  process feel understandable, responsive, and well handled from start to finish.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20" data-testid="company-story">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr]">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-600 dark:text-cyan-300">
                Company Story
              </p>
              <h2 className="mt-3 text-3xl font-black text-slate-950 sm:text-4xl dark:text-white">
                A service business built around making technology easier to work with.
              </h2>
            </div>
            <div className="space-y-6 text-base leading-8 text-slate-600 dark:text-slate-300">
              <p>
                Connectify IT Services supports clients who need dependable technical help without
                the usual confusion. That includes everything from day-to-day support and networking
                through to websites, cloud services, system administration, and hardware-related
                installations.
              </p>
              <p>
                The business is shaped around practical delivery. That means understanding the real
                problem first, recommending a sensible path forward, and then doing the work with
                care and clarity instead of overcomplicating the process.
              </p>
              <p>
                Across multiple cities and broader Australian service regions, the aim stays
                consistent: provide technology services that feel reliable, responsive, and worth
                trusting again when the next job comes up.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-slate-200/70 bg-slate-950 py-20 text-white dark:border-white/10" data-testid="values-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-cyan-300">
              Why Clients Choose Connectify
            </p>
            <h2 className="mt-3 text-3xl font-black sm:text-4xl">
              The work is grounded in service quality, technical confidence, and real follow-through.
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {values.map((value, index) => (
              <div
                key={value.title}
                data-testid={`value-card-${index}`}
                className="rounded-[1.75rem] border border-white/10 bg-white/5 p-8 backdrop-blur"
              >
                <div className="flex items-start gap-4">
                  <div className="rounded-2xl bg-gradient-to-br from-sky-400 to-cyan-300 p-3 text-slate-950">
                    <value.icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold">{value.title}</h3>
                    <p className="mt-3 text-sm leading-7 text-slate-300">{value.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20" data-testid="expertise-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-600 dark:text-cyan-300">
                Expertise
              </p>
              <h2 className="mt-3 text-3xl font-black text-slate-950 sm:text-4xl dark:text-white">
                Capability across support, systems, installs, and digital delivery.
              </h2>
            </div>
            <p className="max-w-xl text-base leading-7 text-slate-600 dark:text-slate-300">
              The service offering is broad because client needs are broad. Connectify works across
              the practical areas that keep homes and businesses functioning well.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {expertiseItems.map((expertise, index) => (
              <div
                key={expertise}
                data-testid={`expertise-item-${index}`}
                className="group rounded-[1.75rem] border border-slate-200 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(241,245,249,0.95))] p-6 transition duration-300 hover:-translate-y-1 hover:border-sky-300 hover:shadow-[0_24px_65px_-28px_rgba(14,165,233,0.55)] dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(15,23,42,0.88),rgba(15,23,42,0.58))] dark:hover:border-cyan-300/60 dark:hover:shadow-[0_24px_65px_-28px_rgba(34,211,238,0.35)]"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-sky-500 to-cyan-400 text-white shadow-lg">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <p className="font-medium text-slate-900 dark:text-white">{expertise}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-20 pt-6" data-testid="about-cta">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="overflow-hidden rounded-[2rem] bg-[linear-gradient(135deg,#0f172a_0%,#0f766e_100%)] p-10 text-white shadow-[0_35px_90px_-35px_rgba(15,23,42,0.8)] sm:p-14">
            <div className="grid gap-10 lg:grid-cols-[1fr_auto] lg:items-center">
              <div className="max-w-2xl">
                <p className="text-sm font-semibold uppercase tracking-[0.24em] text-cyan-200">
                  Let’s Work Together
                </p>
                <h2 className="mt-4 text-3xl font-black sm:text-4xl">
                  If you need dependable technical help, let’s talk about the right next step.
                </h2>
                <p className="mt-4 text-base leading-7 text-slate-200">
                  Whether it starts with support, an installation, or a bigger project, Connectify
                  is set up to help you move from uncertainty to a clear plan.
                </p>
              </div>

              <Link
                to="/contact"
                data-testid="about-contact-btn"
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-8 py-4 font-semibold text-slate-950 transition duration-300 hover:-translate-y-1 hover:bg-slate-100"
              >
                <span>Get in touch</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
